import * as Sentry from "@sentry/nextjs"
import { Label, Radio, Spinner, TextInput, Tooltip } from "flowbite-react"
import { useFormikContext } from "formik"
import React, { ReactNode, useMemo, useState } from "react"

import { DollarIcon } from "@assets/icons"
import FormGroup from "@components/elements/FormGroup"
import useAuth from "@hooks/useAuth"
import CloseCircleIcon from "@icons/CloseCircle"
import { CartItemDetailsType } from "@modules/tenants/OrdersPage/Components/AddOrderModal"
import * as OrderServices from "@mono/essentials/services/OrderServices"
import { PaymentTypes, ValidateGiftcardCodeResponseType } from "@mono/essentials/types/CartTypes"
import { PayOrderRequestType } from "@mono/essentials/types/OrderTypes"
import { getDomainCurrency } from "@mono/essentials/utils/TenantHelper"

import { PaymentAmountDetails } from "../PaymentModal"
import { StripeCreditCardFormAttributes } from "../StripeCreditCardForm/StripeCreditCardForm"

interface PaymentAmountCardProps {
  className?: string
  cartId?: string
  cartDetails?: CartItemDetailsType
  onSuccess?: () => Promise<void>
  paymentAmountDetails?: PaymentAmountDetails
  additionalFields?: ReactNode
  enableCustomAmount?: boolean
  payOrderTipDetails?: PayOrderRequestType["tip"]
  onlyTipPayment?: boolean
  giftcardDetails?: ValidateGiftcardCodeResponseType["giftcard"]
  enableSaveCard?: boolean
}

const PaymentAmountCard = ({
  className,
  cartDetails,
  cartId,
  onSuccess,
  paymentAmountDetails,
  additionalFields,
  enableCustomAmount = false,
  payOrderTipDetails,
  onlyTipPayment,
  giftcardDetails,
  enableSaveCard = false,
}: PaymentAmountCardProps) => {
  const { domainDetails, publicDomainDetails } = useAuth()
  const getcurrency = decodeURIComponent(getDomainCurrency())
  const [isTipUpdating, setIsTipUpdating] = useState(false)
  const { values, setFieldValue, getFieldProps, errors, touched } =
    useFormikContext<StripeCreditCardFormAttributes>()

  const handleRemoveTipping = async () => {
    if (cartId && cartDetails) {
      try {
        setIsTipUpdating(true)
        await OrderServices.updateCart(cartId, {
          ...cartDetails,
          tips: [],
        })
        await onSuccess?.()
      } catch (e) {
        Sentry.captureException(e)
        console.log("Remove tip error: ", e)
      } finally {
        setIsTipUpdating(false)
      }
    } else {
      console.error("Cannot remove the tip due to missing params")
    }
  }

  return (
    <div className={`flex flex-col mb-8 mx-6 p-6 bg-white rounded-md gap-4 ${className ?? ""}`}>
      {!giftcardDetails ? (
        <>
          <div className={"flex flex-row items-center justify-start gap-5"}>
            {paymentAmountDetails?.isDepositEnabledForItems ? (
              <div className={"flex items-center gap-2"} key={PaymentTypes.DEPOSIT_AMOUNT}>
                <Radio
                  id={PaymentTypes.DEPOSIT_AMOUNT}
                  name={"paymentType"}
                  value={PaymentTypes.DEPOSIT_AMOUNT}
                  checked={Boolean(
                    values.paymentType && values.paymentType === PaymentTypes.DEPOSIT_AMOUNT
                  )}
                  onChange={() => {
                    setFieldValue("amount", paymentAmountDetails?.depostAmountWithTip)
                    setFieldValue("paymentType", PaymentTypes.DEPOSIT_AMOUNT)
                  }}
                />
                <Label
                  htmlFor={PaymentTypes.DEPOSIT_AMOUNT}
                  className={"text12Medium text-neutral-900"}
                >
                  Deposit Amount
                </Label>
              </div>
            ) : null}
            {paymentAmountDetails?.isDepositEnabledForItems || enableSaveCard ? (
              <div className={"flex items-center gap-2"} key={PaymentTypes.FULL_AMOUNT}>
                <Radio
                  id={PaymentTypes.FULL_AMOUNT}
                  name={"paymentType"}
                  value={PaymentTypes.FULL_AMOUNT}
                  checked={Boolean(
                    values.paymentType && values.paymentType === PaymentTypes.FULL_AMOUNT
                  )}
                  onChange={() => {
                    setFieldValue("amount", paymentAmountDetails?.totalAmountWithTip)
                    setFieldValue("paymentType", PaymentTypes.FULL_AMOUNT)
                  }}
                />
                <Label
                  htmlFor={PaymentTypes.FULL_AMOUNT}
                  className={"text12Medium text-neutral-900"}
                >
                  Full Amount
                </Label>
              </div>
            ) : null}
            {enableSaveCard && (
              <div className={"flex items-center gap-2"} key={PaymentTypes.SAVE_CARD}>
                <Radio
                  id={PaymentTypes.SAVE_CARD}
                  name={"paymentType"}
                  value={PaymentTypes.SAVE_CARD}
                  checked={Boolean(
                    values.paymentType && values.paymentType === PaymentTypes.SAVE_CARD
                  )}
                  onChange={() => {
                    setFieldValue("amount", 0)
                    setFieldValue("paymentType", PaymentTypes.SAVE_CARD)
                  }}
                  disabled={Boolean(values?.isUsingSavedCard)}
                />
                <Label
                  htmlFor={PaymentTypes.SAVE_CARD}
                  className={`text12Medium ${values?.isUsingSavedCard ? "text-neutral-400" : "text-neutral-900"}`}
                >
                  Save Card Pay Later
                </Label>
              </div>
            )}
          </div>
          <div className={"text14Regular text-neutral-500 font-light"}>
            {values.paymentType === PaymentTypes.DEPOSIT_AMOUNT
              ? `Collect only the deposit amount upfront, with the remaining balance to be paid later.`
              : values.paymentType === PaymentTypes.FULL_AMOUNT
                ? `Collect the total payment upfront to finalize the booking with no outstanding balance.`
                : `The system saves the customer’s card details and allows payment to be processed later.`}
          </div>
        </>
      ) : null}

      {values.paymentType !== PaymentTypes.SAVE_CARD && (
        <>
          <FormGroup label={"Amount"} name={"amount"} requiredField className={""}>
            <div className={"relative w-full"}>
              <TextInput
                type={"tel"}
                min={0}
                {...getFieldProps("amount")}
                placeholder={"10.00"}
                color={errors.amount && touched.amount ? "failure" : undefined}
                className={`${!enableCustomAmount ? "bg-[#E7E7E8]" : "bg-transparent"} rounded-md`}
                icon={() => <DollarIcon className={"text-neutral-400"} width={14} height={14} />}
                autoComplete={"off"}
                disabled={!enableCustomAmount || onlyTipPayment}
              />
            </div>
          </FormGroup>

          {additionalFields ?? ""}

          <div className={"border-t border-dashed border-neutral-300"} />
          <div className={"flex justify-between text14Medium text-neutral-1000"}>
            <div className={"flex items-center gap-2"}>
              {paymentAmountDetails?.totalTip ? (
                <>
                  <div>
                    {values.paymentType === PaymentTypes.DEPOSIT_AMOUNT
                      ? paymentAmountDetails?.depositAmountFormatted
                      : paymentAmountDetails?.totalAmountFormatted}{" "}
                    {`+ ${paymentAmountDetails?.totalTipFormatted} tip`}
                  </div>
                  {!onlyTipPayment ? (
                    <>
                      {isTipUpdating ? (
                        <Spinner size={"sm"} light />
                      ) : (
                        <Tooltip content={"Remove Tip"} trigger={"hover"} placement={"top"}>
                          <CloseCircleIcon
                            className={"cursor-pointer"}
                            onClick={handleRemoveTipping}
                          />
                        </Tooltip>
                      )}
                    </>
                  ) : null}
                </>
              ) : null}
            </div>
            <div className={"flex items-center gap-1"}>
              {`Total Pay: ${
                onlyTipPayment
                  ? ""
                  : enableCustomAmount
                    ? `${domainDetails?.currency_symbol ?? publicDomainDetails?.currency_symbol ?? "$"}${parseFloat(`${values.amount ? values.amount : "0.0"}`).toFixed(2)}`
                    : giftcardDetails
                      ? giftcardDetails.remaining_total.amount_formatted
                      : values.paymentType === PaymentTypes.DEPOSIT_AMOUNT
                        ? paymentAmountDetails?.depostAmountWithTipFormatted
                        : paymentAmountDetails?.totalAmountWithTipFormatted
              } ${
                payOrderTipDetails
                  ? ` ${onlyTipPayment ? "" : "+"} ${
                      payOrderTipDetails.type === "percent"
                        ? `${getcurrency}${parseFloat(
                            `${(((paymentAmountDetails?.subTotalAmount ? paymentAmountDetails.subTotalAmount : paymentAmountDetails?.totalAmount ? paymentAmountDetails.totalAmount : paymentAmountDetails?.totalAmountWithTip) ?? 0) * parseFloat(`${payOrderTipDetails.value}`)) / 100}`
                          ).toFixed(2)} tip`
                        : `${getcurrency}${parseFloat(`${payOrderTipDetails.value}`)} tip`
                    }`
                  : ""
              }`}
              {!onlyTipPayment && !enableCustomAmount && Boolean(giftcardDetails) ? (
                <span className={"text-green-700 text12Semibold"}>(Giftcard Applied)</span>
              ) : null}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default PaymentAmountCard
